import { useState, useEffect, useCallback, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import {
	useGetMessageListQuery,
	useDeleteMessageMutation,
	useSetUserTimeoutMutation,
	useSetChatStatusMutation,
} from '@/store/api';
import { toast } from '@/components/ui/use-toast';
import { asApiError } from '@/utils';
import {
	ChatMessageTargetType,
	ChatSocketType,
	JOIN_CHAT_ROOM,
	ON_CHAT,
	LEAVE_CHAT_ROOM,
} from '@sportspredict/shared';
import { CHAT_PAGE_SIZE } from '@/constants';

export function useEventChat({
	socket,
	eventId,
	activeTab,
	setLobbies,
}: {
	socket: any;
	eventId: string | null;
	activeTab: string;
	setLobbies: (updater: any) => void;
}) {
	const [selectedChatLobby, setSelectedChatLobby] = useState('');
	const [chatTargetType, setChatTargetType] = useState(ChatMessageTargetType.Lobby);
	const chatTargetId = selectedChatLobby;

	const [before, setBefore] = useState<any>(undefined);
	const [messages, setMessages] = useState<any[]>([]);
	const [liveMessages, setLiveMessages] = useState<any[]>([]);

	const shouldFetch =
		chatTargetType === ChatMessageTargetType.Lobby ? !!selectedChatLobby : !!eventId;

	const isChatsTabActive = activeTab === 'chats';
	const [hasOpenedChatsTab, setHasOpenedChatsTab] = useState(false);

	useEffect(() => {
		if (isChatsTabActive && !hasOpenedChatsTab) {
			setHasOpenedChatsTab(true);
		}
	}, [isChatsTabActive, hasOpenedChatsTab]);

	const { data, isFetching } = useGetMessageListQuery(
		shouldFetch && hasOpenedChatsTab
			? {
					target_type: chatTargetType,
					targetIds:
						chatTargetType === ChatMessageTargetType.Lobby ? [chatTargetId, eventId] : [eventId],
					limit: CHAT_PAGE_SIZE,
					...(before ? { before } : {}),
				}
			: skipToken
	);

	const pageMessages = data?.messages ?? [];
	const hasMore = data?.hasMore ?? false;

	useEffect(() => {
		setMessages(pageMessages);
		setLiveMessages([]);
	}, [selectedChatLobby]);

	useEffect(() => {
		setBefore(null);
	}, [selectedChatLobby, chatTargetId]);

	useEffect(() => {
		if (!pageMessages.length) return;

		setMessages((prev) => {
			const existingIds = new Set(prev.map((m) => m.id));
			const newOnes = pageMessages.filter((m: any) => !existingIds.has(m.id));

			return before ? [...newOnes, ...prev] : pageMessages;
		});
	}, [pageMessages, before]);

	const loadMore = useCallback(() => {
		if (!hasMore) return;
		const oldestDate = messages.reduce(
			(min, m) => (new Date(m.created_date) < new Date(min) ? m.created_date : min),
			messages[0].created_date
		);

		setBefore(oldestDate);
	}, [messages, hasMore]);

	const [selectedChatUser, setSelectedChatUser] = useState<any>(null);
	const [chatUserMessages, setChatUserMessages] = useState<any[]>([]);
	const [chatUserMessagesCount, setChatUserMessagesCount] = useState(0);
	const [chatUserLobbyInfo, setChatUserLobbyInfo] = useState<any>(null);

	const { data: chatUserMessagesData } = useGetMessageListQuery(
		selectedChatUser
			? {
					target_type: chatTargetType,
					targetIds: [chatTargetId],
					userId: selectedChatUser.user_id,
					limit: CHAT_PAGE_SIZE,
				}
			: skipToken
	);

	useEffect(() => {
		setChatUserMessages(chatUserMessagesData?.messages || []);
		setChatUserMessagesCount(chatUserMessagesData?.count || 0);
	}, [chatUserMessagesData]);

	useEffect(() => {
		if (!socket || !chatTargetId) return;
		if (!hasOpenedChatsTab) return;
		setLiveMessages([]);
		const handler = ({ type, data: msg }: any) => {
			setMessages((prev) =>
				prev.map((m) => {
					if (m.id !== msg.id) return m;
					if (type === ChatSocketType.DeleteMessage)
						return { ...m, deleted_date: new Date().toISOString() };
					return msg;
				})
			);

			setLiveMessages((prev) => {
				if (type === ChatSocketType.NewMessage) {
					if (prev.some((m) => m.id === msg.id)) return prev;
					return [...prev, msg];
				}
				return prev.map((m) =>
					m.id === msg.id
						? type === ChatSocketType.DeleteMessage
							? { ...m, deleted_date: new Date().toISOString() }
							: msg
						: m
				);
			});

			if (selectedChatUser && type === ChatSocketType.DeleteMessage) {
				setChatUserMessages((prev) =>
					prev.map((m) => (m.id === msg.id ? { ...m, deleted_date: new Date().toISOString() } : m))
				);
			}

			if (selectedChatUser && msg.user_id === selectedChatUser.user_id) {
				setChatUserMessages((prev) => {
					if (type === ChatSocketType.NewMessage) {
						if (prev.some((m) => m.id === msg.id)) return prev;
						return [...prev, msg];
					}
					if (type === ChatSocketType.UpdateMessage) {
						return prev.map((m) => (m.id === msg.id ? msg : m));
					}
					return prev;
				});

				if (type === ChatSocketType.NewMessage) {
					setChatUserMessagesCount((prev) => prev + 1);
				}
			}
		};

		socket.emit(JOIN_CHAT_ROOM, { targetId: chatTargetId });
		socket.on(ON_CHAT, handler);
		return () => {
			socket.emit(LEAVE_CHAT_ROOM, { targetId: chatTargetId });
			socket.off(ON_CHAT, handler);
		};
	}, [socket, chatTargetId, selectedChatUser]);

	const messageList = useMemo(() => {
		const liveIds = new Set(liveMessages.map((m) => m.id));
		return [...messages.filter((m) => !liveIds.has(m.id)), ...liveMessages];
	}, [messages, liveMessages]);

	const [deleteMessage] = useDeleteMessageMutation();
	const handleDeleteMessage = async (message: any) => {
		try {
			await deleteMessage({ id: message.id }).unwrap();

			toast({
				title: 'Success',
				description: 'Message has been deleted',
			});
		} catch (error: unknown) {
			toast({
				title: 'Error',
				description: asApiError(error).message || 'Failed to delete message',
				variant: 'destructive',
			});
		}
	};

	const [setUserTimeout] = useSetUserTimeoutMutation();
	const handleTimeoutUser = async ({ userId, lobbyId, isUserTimedOut }: any) => {
		try {
			const action = isUserTimedOut ? 'unTimeout' : 'timeout';
			const updatedLobbyUser = await setUserTimeout({ lobbyId, userId, action }).unwrap();

			setLobbies((prev: any[]) =>
				prev.map((l) =>
					l.id === lobbyId
						? {
								...l,
								lobby_users: l.lobby_users.map((u: any) =>
									u.user_id === updatedLobbyUser.user_id ? { ...u, ...updatedLobbyUser } : u
								),
							}
						: l
				)
			);

			if (userId === updatedLobbyUser.user_id && lobbyId === updatedLobbyUser.lobby_id) {
				setChatUserLobbyInfo(updatedLobbyUser);
			}

			toast({
				title: 'Success',
				description: isUserTimedOut ? 'User timeout has been removed' : 'User has been timed out',
			});
		} catch (error: unknown) {
			toast({
				title: 'Error',
				description: asApiError(error).message || 'Failed to update user timeout',
				variant: 'destructive',
			});
		}
	};

	const [setChatStatus] = useSetChatStatusMutation();
	const handleSetChatStatus = async (lobby: any) => {
		try {
			await setChatStatus({ lobbyId: lobby.id }).unwrap();
			setLobbies((prev: any[]) =>
				prev.map((l) => (l.id === lobby.id ? { ...l, is_chat_closed: !lobby.is_chat_closed } : l))
			);

			toast({
				title: 'Success',
				description: `Chat has been ${lobby.is_chat_closed ? 'opened' : 'closed'}`,
			});
		} catch (error: unknown) {
			toast({
				title: 'Error',
				description: asApiError(error).message || 'Failed to update chat status',
				variant: 'destructive',
			});
		}
	};

	const handleSelectedChatUser = (user: any) => {
		setSelectedChatUser(user);
		if (user) {
			setChatUserLobbyInfo(user);
		} else {
			setChatUserLobbyInfo(null);
		}
	};

	const handleSetChatTargetType = (type: any) => {
		setChatTargetType(type);
		setSelectedChatUser(null);
	};

	return {
		selectedChatLobby,
		setSelectedChatLobby,
		chatTargetType,
		chatTargetId,
		messageList,
		loadMore,
		isFetching,
		hasMore,
		selectedChatUser,
		setSelectedChatUser,
		chatUserMessages,
		chatUserMessagesCount,
		chatUserLobbyInfo,
		setMessages,
		handleDeleteMessage,
		handleTimeoutUser,
		handleSetChatStatus,
		handleSelectedChatUser,
		handleSetChatTargetType,
	};
}
